import { useEffect, useMemo, useState } from "react";

function AiSummarySparkleIcon({ className = "w-5 h-5" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 2l1.2 4.2L17.5 7.5 13.2 8.7 12 13l-1.2-4.3L6.5 7.5l4.3-1.3L12 2zm7 9l.9 3.1L23 15l-3.1.9L19 19l-.9-3.1L15 15l3.1-.9L19 11zm-14 0l.9 3.1L9 15l-3.1.9L5 19l-.9-3.1L1 15l3.1-.9L5 11z" />
    </svg>
  );
}

/**
 * Inline AI summary card — expands in place (no full-page blur modal).
 */
export default function AiSummaryCard({
  narrative,
  narrativeLoading,
  narrativeSource,
  onGenerate,
  dataReady,
  emptyHint = "Summary will generate automatically.",
  loadingHint = "Loading…",
  renderParagraph,
  readDisabled = false,
  pdfNarrativeAttr = false,
}) {
  const [expanded, setExpanded] = useState(false);

  const paragraphs = useMemo(
    () => (narrative ? narrative.split(/\n\n+/).filter(Boolean) : []),
    [narrative]
  );

  const sourceLabel =
    narrativeSource === "gemini"
      ? "Gemini"
      : narrativeSource === "rule_based"
        ? "Auto-generated"
        : null;

  useEffect(() => {
    if (!expanded) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setExpanded(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [expanded]);

  const previewText = paragraphs[0]
    ? String(paragraphs[0]).replace(/\*\*/g, "").slice(0, 120) +
      (String(paragraphs[0]).length > 120 ? "…" : "")
    : null;

  const bodyLoading = (
    <div className="space-y-3 animate-pulse w-full">
      <div className="h-3.5 bg-slate-200 dark:bg-slate-700 rounded w-full" />
      <div className="h-3.5 bg-slate-200 dark:bg-slate-700 rounded w-[95%]" />
      <div className="h-3.5 bg-slate-200 dark:bg-slate-700 rounded w-[88%]" />
      <div className="h-3.5 bg-slate-200 dark:bg-slate-700 rounded w-[72%]" />
    </div>
  );

  const bodyContent = narrative ? (
    <div
      className={`w-full transition-opacity duration-200 ${narrativeLoading ? "opacity-60" : ""}`}
      {...(pdfNarrativeAttr ? { "data-pdf-narrative": true } : {})}
    >
      {paragraphs.map((para, index) => (
        <p
          key={index}
          className={
            index === 0
              ? "text-[15px] sm:text-base leading-[1.75] text-slate-800 dark:text-slate-100"
              : "mt-4 text-[15px] leading-[1.75] text-slate-700 dark:text-slate-200"
          }
        >
          {renderParagraph ? renderParagraph(para) : para}
        </p>
      ))}
    </div>
  ) : (
    <p className="text-sm text-slate-500 dark:text-slate-400 max-w-3xl">
      {dataReady ? emptyHint : loadingHint}
    </p>
  );

  return (
    <div
      className={`mb-5 nexus-ai-insight-card rounded-2xl border border-brand/20 bg-gradient-to-br from-brand/5 to-white dark:from-cyan-950/30 dark:to-slate-900/40 dark:border-cyan-500/20 shadow-sm min-w-0 transition-shadow ${
        expanded ? "ring-2 ring-brand/15 dark:ring-cyan-500/20 shadow-md" : ""
      }`}
      data-export-ignore
    >
      <div className="p-4">
        <div className="flex items-start gap-3 min-w-0">
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-brand/10 text-brand dark:bg-cyan-500/15 dark:text-cyan-300 shrink-0 mt-0.5">
            <AiSummarySparkleIcon className="w-4 h-4" />
          </span>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className="text-sm font-semibold text-brand dark:text-cyan-300">AI Summary</span>
              {narrativeLoading ? (
                <span className="text-xs text-slate-400 animate-pulse">Generating…</span>
              ) : sourceLabel ? (
                <span className="text-[11px] text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-800 rounded-full px-2 py-0.5">
                  {sourceLabel}
                </span>
              ) : null}
            </div>
            {!expanded ? (
              narrativeLoading && !narrative ? (
                <div className="space-y-2 animate-pulse">
                  <div className="h-3 bg-slate-200 dark:bg-slate-700 rounded w-full" />
                  <div className="h-3 bg-slate-200 dark:bg-slate-700 rounded w-[80%]" />
                </div>
              ) : previewText ? (
                <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed line-clamp-2">
                  {previewText}
                </p>
              ) : (
                <p className="text-sm text-slate-400 dark:text-slate-500">
                  {dataReady
                    ? narrativeLoading
                      ? "Generating summary…"
                      : emptyHint
                    : loadingHint}
                </p>
              )
            ) : (
              <div className="mt-2 max-h-[min(420px,52vh)] overflow-y-auto overscroll-contain pr-1 -mr-1 rounded-xl border border-slate-100 dark:border-slate-700/80 bg-white/70 dark:bg-slate-950/50 px-4 py-3">
                {narrativeLoading && !narrative ? bodyLoading : bodyContent}
              </div>
            )}
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-1.5 shrink-0">
            {narrative ? (
              <button
                type="button"
                onClick={onGenerate}
                disabled={!dataReady || narrativeLoading}
                className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-brand dark:text-cyan-300 bg-brand/10 dark:bg-cyan-500/10 hover:bg-brand/20 dark:hover:bg-cyan-500/20 rounded-lg transition-colors disabled:opacity-40 disabled:pointer-events-none"
              >
                {narrativeLoading ? "Refreshing…" : "Refresh"}
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              disabled={readDisabled || (!narrative && narrativeLoading)}
              className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-slate-600 dark:text-slate-200 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg transition-colors disabled:opacity-40 disabled:pointer-events-none"
            >
              {expanded ? "Collapse" : "Read"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export { AiSummarySparkleIcon };
